/**
 * Multi-account registry — lets a user stay signed into several accounts at once
 * and switch between them.
 *
 * How it works: the app's existing auth (the axios interceptor, AuthContext,
 * token refresh) all read the canonical localStorage keys `accessToken` /
 * `refreshToken` / `tokenExpiry`. Those ALWAYS mirror the *active* account, so
 * nothing else in the app needs to know about multi-account.
 *
 * On top of that we keep `pr.accounts` — a list of every signed-in account with
 * its own token snapshot + a little profile blob for the switcher UI — and
 * `pr.activeAccountId`. Switching just copies an account's tokens into the
 * canonical keys and reloads; the whole app re-inits cleanly for the new user.
 */

const ACCOUNTS_KEY = 'pr.accounts';
const ACTIVE_KEY = 'pr.activeAccountId';
const TOKEN_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';
const EXPIRY_KEY = 'tokenExpiry';

export function accountIdOf(user) {
  return user?.userId || user?.id || null;
}

export function profileFromUser(user) {
  if (!user) return {};
  // The /users/me + login user carries its profile nested under `profile`
  // (displayName/avatarUrl/handle); some call sites pass a flattened profile.
  // Read the nested one first, then fall back to top-level fields.
  const p = user.profile || {};
  return {
    displayName: p.displayName || user.displayName || user.name || user.email || 'Account',
    avatarUrl: p.avatarUrl || user.avatarUrl || user.avatar || null,
    handle: p.handle || user.handle || null,
    role: user.role || p.role || null,
    email: user.email || null,
  };
}

export function getAccounts() {
  try {
    const raw = JSON.parse(localStorage.getItem(ACCOUNTS_KEY));
    return Array.isArray(raw) ? raw.filter((a) => a && a.id) : [];
  } catch {
    return [];
  }
}

function saveAccounts(list) {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list));
}

export function getActiveId() {
  return localStorage.getItem(ACTIVE_KEY);
}

export function setActiveId(id) {
  if (id) localStorage.setItem(ACTIVE_KEY, id);
  else localStorage.removeItem(ACTIVE_KEY);
}

/** Public (token-free) view of the saved accounts for the switcher UI. */
export function listAccountsForUi() {
  const activeId = getActiveId();
  return getAccounts().map((a) => ({ id: a.id, profile: a.profile || {}, isActive: a.id === activeId }));
}

function canonicalTokens() {
  return {
    accessToken: localStorage.getItem(TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_KEY),
    tokenExpiry: localStorage.getItem(EXPIRY_KEY),
  };
}

/** Add or update an account entry. Pass tokens to overwrite them; omit to keep. */
export function upsertAccount(id, tokens, profile) {
  if (!id) return;
  const list = getAccounts();
  const idx = list.findIndex((a) => a.id === id);
  const prev = idx >= 0 ? list[idx] : {};
  const entry = {
    id,
    accessToken: tokens?.accessToken ?? prev.accessToken ?? null,
    refreshToken: tokens?.refreshToken ?? prev.refreshToken ?? null,
    tokenExpiry: tokens?.tokenExpiry ?? prev.tokenExpiry ?? null,
    profile: { ...(prev.profile || {}), ...(profile || {}) },
  };
  if (idx >= 0) list[idx] = entry;
  else list.push(entry);
  saveAccounts(list);
}

export function removeAccount(id) {
  saveAccounts(getAccounts().filter((a) => a.id !== id));
}

/**
 * Register the currently-signed-in user as the active account, snapshotting the
 * canonical tokens. Call after every successful auth (login / register / init).
 */
export function registerActive(user) {
  const id = accountIdOf(user);
  if (!id) return;
  upsertAccount(id, canonicalTokens(), profileFromUser(user));
  setActiveId(id);
}

/** Persist the live canonical tokens back onto the active account (after a refresh). */
export function syncActiveTokensFromCanonical() {
  const id = getActiveId();
  if (!id) return;
  const list = getAccounts();
  const idx = list.findIndex((a) => a.id === id);
  if (idx < 0) return;
  const t = canonicalTokens();
  list[idx].accessToken = t.accessToken;
  list[idx].refreshToken = t.refreshToken;
  list[idx].tokenExpiry = t.tokenExpiry;
  saveAccounts(list);
}

/** Make an account entry the active one by copying its tokens into the canonical keys. */
export function activateCanonical(account) {
  if (!account) return;
  // "Acting as <Page>" belongs to the account that was signed in when it was
  // chosen. Carrying it across a switch left the new account wearing a page it
  // may not manage at all — the banner said ACTING AS SKAHA SURF for someone
  // with no access to it. Changing who you are always drops the persona.
  try { localStorage.removeItem('pr.activePage'); } catch { /* storage off */ }
  if (account.accessToken) localStorage.setItem(TOKEN_KEY, account.accessToken);
  else localStorage.removeItem(TOKEN_KEY);
  if (account.refreshToken) localStorage.setItem(REFRESH_KEY, account.refreshToken);
  else localStorage.removeItem(REFRESH_KEY);
  if (account.tokenExpiry) localStorage.setItem(EXPIRY_KEY, account.tokenExpiry);
  else localStorage.removeItem(EXPIRY_KEY);
  setActiveId(account.id);
}

export function getAccount(id) {
  return getAccounts().find((a) => a.id === id) || null;
}

/** Wipe the whole registry (used on a full sign-out of the last account). */
export function clearRegistry() {
  localStorage.removeItem(ACCOUNTS_KEY);
  localStorage.removeItem(ACTIVE_KEY);
}

/**
 * Token expiry from whatever the auth response actually carries. Login returns
 * `expiresAt` — an absolute timestamp — not the `expiresIn` seconds the old
 * helper assumed, so it always produced null and left the account with no
 * expiry at all. Both shapes are accepted now.
 */
export function expiryFromAuth(auth) {
  if (!auth) return null;
  if (auth.expiresAt) {
    const t = new Date(auth.expiresAt).getTime();
    if (!Number.isNaN(t)) return String(t);
  }
  return auth.expiresIn ? String(Date.now() + auth.expiresIn * 1000) : null;
}
